import { FunctionComponent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogTitle, DialogContent, DialogAction } from "@panviva/panviva-react-ui";
import { WithOktaAuthProps } from "src/ts/Auth/duck/Models";
import i18n from "../../../../src/ts/i18n";
import { getResponseVersionsThunk, ResetResponseVersionsAction } from "../../../../src/ts/Responses/duck/Actions";
import { IStoreState } from "src/ts/Model";
import PaginationClient from "../../../../src/ts/Shared/Pagination/PaginationClient";
import { LoadingIndicator } from "../../../../src/ts/Shared/LoadingIndicator";
import "./HistoricalResponseListModal.scss";

interface IHistoricalResponseListModalProps {
    responseId: string;
    documentId: string;
    authState: WithOktaAuthProps["authState"];
    onCloseIconClick: () => void;
}

export const HistoricalResponseListModal: FunctionComponent<IHistoricalResponseListModalProps> = ({ responseId, documentId, authState, onCloseIconClick }) => {

    const dispatch = useDispatch();
    const versionsList = useSelector((state: IStoreState) => state.responseVersions.responses);
    const fetchingVersions = useSelector((state: IStoreState) => state.responseVersions.fetching);
    const [openModalState, setOpenModalState] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [paginatedList, setPaginatedList] = useState<Api.IHistoricalVersionsModel[]>([]); // Pagination logics can be added here in future

    const totalItems = versionsList.versions ? versionsList.versions.length : 0;
    const [totalPages, setTotalPages] = useState(Math.ceil(totalItems / pageSize));

    useEffect(() => {
        dispatch(getResponseVersionsThunk(responseId, authState));
    }, [responseId, dispatch, authState]);

    useEffect(() => {
        setPaginatedList(sortedData(versionsList.versions || []).slice((currentPage - 1) * pageSize, currentPage * pageSize));
        setTotalPages(Math.ceil((versionsList.versions || []).length / pageSize));
    }, [versionsList, pageSize, currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const sortedData = (data: Api.IHistoricalVersionsModel[]) => {
        let newData: Api.IHistoricalVersionsModel[] = JSON.parse(JSON.stringify(data));
        return newData.sort((a, b) => {
            const timeA = new Date(b.lastUpdated).getTime();
            const timeB = new Date(a.lastUpdated).getTime();
            // If times are equal, compare the original string to preserve millisecond precision
            if (timeA === timeB) {
                return b.lastUpdated.localeCompare(a.lastUpdated);
            }
            return timeA - timeB;
        });
    }

    return (
        <Dialog open={openModalState} className="historical-response-list-modal">
            <DialogTitle className="historical-response-list-modal-title">
                {i18n.t("historicalResponseList.modalTitle")}
                <i className="icon icon-close" role="button" tabIndex={0} aria-label="close-dialog" onClick={() => { setOpenModalState(false); onCloseIconClick(); dispatch(new ResetResponseVersionsAction()); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setOpenModalState(false); onCloseIconClick(); dispatch(new ResetResponseVersionsAction()); } }} />
            </DialogTitle>
            <DialogContent dividers className="historical-responses-table-container">
                <table className="table table-responsive historical-response-table">
                    <thead>
                        <tr>
                            <th className="version" scope="col">
                                {i18n.t("historicalResponseList.versionHeading")}
                            </th>
                            <th className="title" scope="col">
                                {i18n.t("historicalResponseList.titleHeading")}
                            </th>
                            <th className="dateModified" scope="col">
                                {i18n.t("historicalResponseList.dateModifiedHeading")}
                            </th>
                            <th className="publishedByUserId" scope="col">
                                {i18n.t("historicalResponseList.publishedByUserIdHeading")}
                            </th>
                        </tr>
                    </thead>
                    <tbody id="artefact-version-table">
                        {paginatedList && paginatedList.length > 0 ? (
                            paginatedList.map((historicalResponse: Api.IHistoricalVersionsModel) => (
                                <tr key={historicalResponse.version}>
                                    <td className="version">
                                        {historicalResponse.version}
                                    </td>
                                    <td className="title">
                                        <a href="" target="_blank" onClick={(e) => { e.preventDefault(); window.open(`/view/${responseId}?documentId=${documentId}&version=${historicalResponse.version}`, '_blank'); }}>{historicalResponse.responseTitle}</a>
                                    </td>
                                    <td className="dateModified">
                                        {new Date(historicalResponse.lastUpdated).toLocaleDateString('en-GB')}
                                    </td>
                                    <td className="publishedByUserId">
                                        {historicalResponse.updatedBy}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td align="center" colSpan={4} className="no-border-bottom">
                                    {fetchingVersions ? <LoadingIndicator busy={true} targetElementId="artefact-version-table" /> : i18n.t("historicalResponseList.noHistoricalData")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </DialogContent>
            <DialogAction>
                <PaginationClient
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </DialogAction>
        </Dialog>
    );
}
